import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildClientDisplayName,
  SERVICE_MODE_OPTIONS,
  SERVICE_PRIORITY_OPTIONS,
  SERVICE_TEMPLATE_FREQUENCY_OPTIONS,
  SERVICE_STOP_ACTION_OPTIONS,
  SERVICE_TRANSPORT_OPTIONS,
  resolveServiceTaskLabel,
} from '../serviceOptions';
import {
  buildLocationSnapshotFromManual,
  buildLocationSnapshotFromOption,
  buildServiceLocationOptions,
  createManualLocationState,
} from '../serviceLocationUtils';

const Icon = ({ path, className = 'w-5 h-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const EMPTY_FORM = {
  clientMode: 'existing',
  clientId: '',
  newClientName: '',
  newClientContactName: '',
  newClientPhone: '',
  newClientEmail: '',
  serviceMode: 'puntual',
  serviceTransport: 'camioneta',
  frequency: 'weekly',
  priority: 'normal',
  notes: '',
};

const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY || '';

const buildStateFromSnapshot = (snapshot = null) => ({
  optionId: '',
  mode: 'manual',
  label: snapshot?.label || '',
  address: snapshot?.address || '',
  locality: snapshot?.locality || '',
  province: snapshot?.province || '',
  postalCode: snapshot?.postalCode || '',
  lat: snapshot?.latlng?.lat ?? '',
  lng: snapshot?.latlng?.lng ?? '',
  saveToSharedBase: true,
  serviceAction: snapshot?.serviceAction || 'entrega',
  actionNotes: snapshot?.actionNotes || '',
});

const createAdditionalStopState = () => ({
  id: `stop-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  ...createManualLocationState(),
  serviceAction: 'entrega',
  actionNotes: '',
});

const createDestinationState = () => ({
  ...createManualLocationState(),
  serviceAction: 'entrega',
  actionNotes: '',
});

const withStopMetadata = (snapshot = null, state = {}, fallbackAction = 'entrega') => {
  if (!snapshot) return null;
  const action = String(state?.serviceAction || snapshot?.serviceAction || fallbackAction).trim() || fallbackAction;
  return {
    ...snapshot,
    serviceAction: action,
    serviceActionLabel: resolveServiceTaskLabel(action),
    actionNotes: String(state?.actionNotes || snapshot?.actionNotes || '').trim(),
  };
};

const summarizeStopActions = (destinationSnapshot = null, additionalStops = []) => {
  const allStops = [destinationSnapshot, ...(Array.isArray(additionalStops) ? additionalStops : [])].filter(Boolean);
  if (allStops.length === 0) {
    return { serviceTask: 'servicio', serviceTaskLabel: 'Servicio' };
  }
  if (allStops.length === 1) {
    const action = String(allStops[0]?.serviceAction || 'entrega').trim() || 'entrega';
    return {
      serviceTask: action,
      serviceTaskLabel: allStops[0]?.serviceActionLabel || resolveServiceTaskLabel(action),
    };
  }
  return {
    serviceTask: 'multi_stop',
    serviceTaskLabel: `${allStops.length} paradas`,
  };
};

const baseFieldClassName =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100';
const readOnlyFieldClassName = `${baseFieldClassName} bg-slate-50 text-slate-600`;

const InputBlock = ({ label, children, helper = '' }) => (
  <label className="block space-y-2">
    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
    {children}
    {helper ? <span className="block text-xs text-slate-400">{helper}</span> : null}
  </label>
);

const CONTACT_TYPE_OPTIONS = [
  { value: 'cliente', label: 'Cliente' },
  { value: 'proveedor', label: 'Proveedor' },
  { value: 'transporte', label: 'Transporte' },
];

const hasCoordinates = (state = {}) => {
  const lat = Number(state?.lat);
  const lng = Number(state?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng);
};

const EndpointSection = ({ title, state, onChange, locationOptions, tone = 'slate' }) => {
  const autocompleteWrapRef = useRef(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [loadingAddressSuggestions, setLoadingAddressSuggestions] = useState(false);
  const selectedOption = locationOptions.find((option) => option.id === state.optionId) || null;
  const geolocationReady = state.mode === 'existing'
    ? Boolean(selectedOption?.latlng && Number.isFinite(Number(selectedOption.latlng.lat)) && Number.isFinite(Number(selectedOption.latlng.lng)))
    : hasCoordinates(state);
  const resolvedLabel = state.mode === 'existing' ? (selectedOption?.label || '') : state.label;
  const resolvedAddress = state.mode === 'existing' ? (selectedOption?.address || '') : state.address;
  const resolvedLocality = state.mode === 'existing' ? (selectedOption?.locality || '') : state.locality;
  const resolvedProvince = state.mode === 'existing' ? (selectedOption?.province || '') : state.province;
  const resolvedPostalCode = state.mode === 'existing' ? (selectedOption?.postalCode || '') : state.postalCode;
  const resolvedLat = state.mode === 'existing' ? (selectedOption?.latlng?.lat ?? '') : state.lat;
  const resolvedLng = state.mode === 'existing' ? (selectedOption?.latlng?.lng ?? '') : state.lng;
  const toneClasses = tone === 'origin'
    ? {
        shell: 'border-cyan-200 bg-[linear-gradient(135deg,#ecfeff_0%,#f8fafc_100%)]',
        badge: 'border-cyan-200 bg-cyan-50 text-cyan-800',
      }
    : {
        shell: 'border-violet-200 bg-[linear-gradient(135deg,#faf5ff_0%,#fff7ed_100%)]',
        badge: 'border-violet-200 bg-violet-50 text-violet-800',
      };

  useEffect(() => {
    if (state.mode !== 'manual' || !GEOAPIFY_API_KEY) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      setLoadingAddressSuggestions(false);
      return undefined;
    }

    const queryText = String(state.address || '').trim();
    const localityText = String(state.locality || '').trim();
    const provinceText = String(state.province || '').trim();
    const shouldQuery = queryText.length >= 3;

    if (!shouldQuery) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      setLoadingAddressSuggestions(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        setLoadingAddressSuggestions(true);
        const searchText = [queryText, localityText, provinceText].filter(Boolean).join(', ');
        const url =
          `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(searchText)}` +
          `&lang=es&limit=5&filter=countrycode:ar&format=json&apiKey=${GEOAPIFY_API_KEY}`;
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Geoapify autocomplete error: ${response.status}`);
        const data = await response.json();
        const results = Array.isArray(data?.results) ? data.results : [];
        setAddressSuggestions(results);
        setShowAddressSuggestions(true);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error cargando sugerencias de dirección en Programados:', error);
          setAddressSuggestions([]);
          setShowAddressSuggestions(false);
        }
      } finally {
        setLoadingAddressSuggestions(false);
      }
    }, 350);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [state.mode, state.address, state.locality, state.province]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!autocompleteWrapRef.current?.contains(event.target)) {
        setShowAddressSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`rounded-[26px] border p-5 ${toneClasses.shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-black tracking-tight text-slate-900">{title}</p>
          <p className="mt-1 text-sm text-slate-500">Guardá el recorrido base para reutilizarlo con un clic.</p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${toneClasses.badge}`}>
          {tone === 'origin' ? 'Punto de salida' : 'Punto operativo'}
        </span>
        <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
          <button
            type="button"
            onClick={() => onChange((current) => ({ ...current, mode: 'existing' }))}
            className={`rounded-2xl px-3 py-2 text-xs font-semibold transition ${state.mode === 'existing' ? 'bg-cyan-50 text-cyan-800' : 'text-slate-500 hover:bg-slate-50'}`}
          >
            Buscar existente
          </button>
          <button
            type="button"
            onClick={() => onChange((current) => ({ ...current, mode: 'manual' }))}
            className={`rounded-2xl px-3 py-2 text-xs font-semibold transition ${state.mode === 'manual' ? 'bg-violet-50 text-violet-800' : 'text-slate-500 hover:bg-slate-50'}`}
          >
            Cargar nuevo
          </button>
        </div>
      </div>

      {state.mode === 'existing' ? (
        <div className="mt-4 space-y-3">
          <select
            value={state.optionId}
            onChange={(event) => {
              const nextOptionId = event.target.value;
              const option = locationOptions.find((entry) => entry.id === nextOptionId) || null;
              onChange((current) => ({
                ...current,
                optionId: nextOptionId,
                label: option?.label || '',
                address: option?.address || '',
                locality: option?.locality || '',
                province: option?.province || '',
                postalCode: option?.postalCode || '',
                lat: option?.latlng?.lat ?? '',
                lng: option?.latlng?.lng ?? '',
              }));
            }}
            className={baseFieldClassName}
          >
            <option value="">Elegí una dirección existente</option>
            {locationOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label} · {option.address}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <InputBlock label="Nombre de referencia">
            <input value={state.label} onChange={(event) => onChange((current) => ({ ...current, label: event.target.value }))} className={baseFieldClassName} placeholder="Nombre corto del punto" />
          </InputBlock>
          <div className="md:col-span-2" ref={autocompleteWrapRef}>
            <InputBlock label="Dirección" helper={!GEOAPIFY_API_KEY ? 'Falta configurar Geoapify para ver sugerencias automáticas.' : 'Escribí calle y número para ver calles sugeridas.'}>
              <div className="relative">
                <input
                  value={state.address}
                  onChange={(event) => onChange((current) => ({ ...current, address: event.target.value }))}
                  onFocus={() => {
                    if (addressSuggestions.length > 0) {
                      setShowAddressSuggestions(true);
                    }
                  }}
                  autoComplete="off"
                  className={`${baseFieldClassName} pr-12`}
                  placeholder="Calle y número"
                />
                {loadingAddressSuggestions ? (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-cyan-600">
                    <Icon
                      path="M4.75 12a7.25 7.25 0 0112.511-4.95M19.25 12a7.25 7.25 0 01-12.511 4.95"
                      className="h-5 w-5 animate-spin"
                    />
                  </div>
                ) : null}
              </div>

              {showAddressSuggestions ? (
                <div className="mt-2 overflow-hidden rounded-2xl border border-cyan-200 bg-white shadow-[0_18px_45px_rgba(6,182,212,0.14)]">
                  {addressSuggestions.length > 0 ? (
                    <div className="max-h-64 overflow-y-auto p-2">
                      {addressSuggestions.map((item, index) => (
                        <button
                          key={`${item.place_id || item.formatted || 'suggestion'}-${index}`}
                          type="button"
                          onClick={() => {
                            onChange((current) => ({
                              ...current,
                              address: item.address_line1 || item.street || item.formatted || current.address,
                              locality: item.city || item.county || item.state_district || current.locality,
                              province: item.state || current.province,
                              postalCode: item.postcode || current.postalCode,
                              lat: item.lat ?? current.lat,
                              lng: item.lon ?? current.lng,
                              label: current.label || item.address_line1 || item.street || 'Destino',
                            }));
                            setAddressSuggestions([]);
                            setShowAddressSuggestions(false);
                          }}
                          className="flex w-full flex-col rounded-2xl px-3 py-3 text-left transition hover:bg-cyan-50"
                        >
                          <p className="font-semibold text-slate-900">
                            {item.address_line1 || item.street || item.formatted || 'Dirección sugerida'}
                          </p>
                          <p className="mt-1 text-sm text-slate-500">
                            {item.formatted || [item.city, item.state, item.country].filter(Boolean).join(', ')}
                          </p>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-3 text-sm text-slate-600">
                      No encontramos sugerencias todavía. Probá con calle, número y localidad.
                    </div>
                  )}
                </div>
              ) : null}
            </InputBlock>
          </div>
          <InputBlock label="Localidad">
            <input value={state.locality} onChange={(event) => onChange((current) => ({ ...current, locality: event.target.value }))} className={baseFieldClassName} placeholder="Localidad" />
          </InputBlock>
          <InputBlock label="Provincia">
            <input value={state.province} onChange={(event) => onChange((current) => ({ ...current, province: event.target.value }))} className={baseFieldClassName} placeholder="Provincia" />
          </InputBlock>
          <InputBlock label="Código postal">
            <input value={state.postalCode} onChange={(event) => onChange((current) => ({ ...current, postalCode: event.target.value }))} className={baseFieldClassName} placeholder="Código postal" />
          </InputBlock>
          <InputBlock label="Latitud">
            <input value={state.lat} onChange={(event) => onChange((current) => ({ ...current, lat: event.target.value }))} className={baseFieldClassName} placeholder="-34.6037" />
          </InputBlock>
          <InputBlock label="Longitud">
            <input value={state.lng} onChange={(event) => onChange((current) => ({ ...current, lng: event.target.value }))} className={baseFieldClassName} placeholder="-58.3816" />
          </InputBlock>
          <label className="md:col-span-2 inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
            <input
              type="checkbox"
              checked={state.saveToSharedBase}
              onChange={(event) => onChange((current) => ({ ...current, saveToSharedBase: event.target.checked }))}
              className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
            />
            Guardar este punto en la base común
          </label>
        </div>
      )}

      <div className="mt-4 rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Validación de ubicación</p>
          <span
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${
              geolocationReady
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-amber-200 bg-amber-50 text-amber-700'
            }`}
          >
            {geolocationReady ? 'Geolocalización lista' : 'Se validará al guardar'}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <InputBlock label="Referencia visible">
            <input value={resolvedLabel} readOnly className={readOnlyFieldClassName} placeholder="Todavía sin referencia" />
          </InputBlock>
          <InputBlock label="Dirección usada">
            <input value={resolvedAddress} readOnly className={readOnlyFieldClassName} placeholder="Todavía sin dirección" />
          </InputBlock>
          <InputBlock label="Localidad">
            <input value={resolvedLocality} readOnly className={readOnlyFieldClassName} placeholder="Sin localidad" />
          </InputBlock>
          <InputBlock label="Provincia">
            <input value={resolvedProvince} readOnly className={readOnlyFieldClassName} placeholder="Sin provincia" />
          </InputBlock>
          <InputBlock label="Código postal">
            <input value={resolvedPostalCode} readOnly className={readOnlyFieldClassName} placeholder="Sin código postal" />
          </InputBlock>
          {state.mode === 'manual' ? (
            <InputBlock label="Guardar como" helper="Esto define si el punto queda en clientes, proveedores o transportes.">
              <select
                value={state.contactType || 'cliente'}
                onChange={(event) => onChange((current) => ({ ...current, contactType: event.target.value }))}
                className={baseFieldClassName}
              >
                {CONTACT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </InputBlock>
          ) : null}
          <div className="grid grid-cols-2 gap-4">
            <InputBlock label="Latitud">
              <input value={resolvedLat} readOnly className={readOnlyFieldClassName} placeholder="Se completa al validar" />
            </InputBlock>
            <InputBlock label="Longitud">
              <input value={resolvedLng} readOnly className={readOnlyFieldClassName} placeholder="Se completa al validar" />
            </InputBlock>
          </div>
        </div>
      </div>
    </div>
  );
};

const StopActionFields = ({ state, onChange, title = 'Acción en este punto' }) => (
  <div className="mt-4 rounded-[22px] border border-slate-200 bg-white/85 p-4 shadow-sm">
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <InputBlock label={title} helper="Definí qué se hace en este destino de la plantilla.">
        <select
          value={state.serviceAction || 'entrega'}
          onChange={(event) => onChange((current) => ({ ...current, serviceAction: event.target.value }))}
          className={baseFieldClassName}
        >
          {SERVICE_STOP_ACTION_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </InputBlock>
      <InputBlock label="Observación de la parada" helper="Esto también se hereda cuando generás un servicio desde la plantilla.">
        <textarea
          value={state.actionNotes || ''}
          onChange={(event) => onChange((current) => ({ ...current, actionNotes: event.target.value }))}
          className={`${baseFieldClassName} min-h-[110px] resize-y`}
          placeholder="Ejemplo: retirar documentación, cobrar, entregar con firma."
        />
      </InputBlock>
    </div>
  </div>
);

const ServiceTemplateModal = ({
  open,
  initialTemplate = null,
  clients = [],
  locations = [],
  saving = false,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [originState, setOriginState] = useState(createManualLocationState());
  const [destinationState, setDestinationState] = useState(createDestinationState());
  const [additionalStops, setAdditionalStops] = useState([]);

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setOriginState(createManualLocationState());
      setDestinationState(createDestinationState());
      setAdditionalStops([]);
      return;
    }

    if (initialTemplate) {
      setForm({
        ...EMPTY_FORM,
        clientMode: 'existing',
        clientId: initialTemplate.clientId || '',
        serviceMode: initialTemplate.serviceMode || 'puntual',
        serviceTransport: initialTemplate.serviceTransport || initialTemplate.serviceType || 'camioneta',
        frequency: initialTemplate.frequency || 'weekly',
        priority: initialTemplate.priority || 'normal',
        notes: initialTemplate.notes || '',
      });
      setOriginState(buildStateFromSnapshot(initialTemplate.originSnapshot));
      setDestinationState(buildStateFromSnapshot(initialTemplate.destinationSnapshot));
      setAdditionalStops(
        Array.isArray(initialTemplate.additionalStops)
          ? initialTemplate.additionalStops.map((stop) => ({
              id: `stop-${Math.random().toString(36).slice(2, 8)}`,
              ...buildStateFromSnapshot(stop),
            }))
          : []
      );
      return;
    }

    setForm(EMPTY_FORM);
    setOriginState(createManualLocationState());
    setDestinationState(createDestinationState());
    setAdditionalStops([]);
  }, [open, initialTemplate]);

  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => buildClientDisplayName(a).localeCompare(buildClientDisplayName(b), 'es')),
    [clients]
  );
  const recurringFrequencyOptions = useMemo(
    () => SERVICE_TEMPLATE_FREQUENCY_OPTIONS.filter((option) => option.value !== 'once'),
    []
  );
  const locationOptions = useMemo(
    () => buildServiceLocationOptions({ contacts: clients, sharedLocations: locations }),
    [clients, locations]
  );
  const isCreatingClient = form.clientMode === 'new';

  if (!open) return null;

  const handleChange = (field) => (event) => {
    const value = event?.target?.value ?? '';
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    const originSnapshot = originState.mode === 'existing'
      ? buildLocationSnapshotFromOption(locationOptions.find((option) => option.id === originState.optionId) || null)
      : buildLocationSnapshotFromManual(originState);

    const destinationSnapshot = destinationState.mode === 'existing'
      ? withStopMetadata(buildLocationSnapshotFromOption(locationOptions.find((option) => option.id === destinationState.optionId) || null), destinationState, 'entrega')
      : withStopMetadata(buildLocationSnapshotFromManual(destinationState), destinationState, 'entrega');
    const additionalStopSnapshots = additionalStops
      .map((stop) =>
        stop.mode === 'existing'
          ? withStopMetadata(buildLocationSnapshotFromOption(locationOptions.find((option) => option.id === stop.optionId) || null), stop, 'entrega')
          : withStopMetadata(buildLocationSnapshotFromManual(stop), stop, 'entrega')
      )
      .filter((stop) => stop?.address);
    const taskSummary = summarizeStopActions(destinationSnapshot, additionalStopSnapshots);

    const selectedClient = sortedClients.find((client) => client.id === form.clientId) || null;

    await onSubmit?.({
      ...form,
      originMode: originState.mode,
      destinationMode: destinationState.mode,
      originState,
      destinationState,
      originSnapshot,
      destinationSnapshot,
      additionalStops,
      additionalStopSnapshots,
      serviceTask: taskSummary.serviceTask,
      serviceTaskLabel: taskSummary.serviceTaskLabel,
      selectedClient,
    });
  };

  return (
    <div className="fixed inset-0 z-[1800] flex items-center justify-center bg-slate-950/60 px-4 py-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.28)]">
        <div className="border-b border-slate-200 bg-[linear-gradient(135deg,#0f172a_0%,#155e75_56%,#cffafe_100%)] px-6 py-5 text-white">
          <div className="flex items-start justify-between gap-4">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-cyan-100">Programados</p>
              <h2 className="mt-2 text-2xl font-black tracking-tight">
                {initialTemplate ? 'Editar plantilla reutilizable' : 'Nueva plantilla reutilizable'}
              </h2>
              <p className="mt-2 text-sm leading-6 text-cyan-50/90">
                Guardamos un viaje tipo para volver a generarlo con un clic sin cargar de nuevo cliente, recorrido y referencia comercial.
              </p>
            </div>
            <button type="button" onClick={saving ? undefined : onClose} className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-white transition hover:bg-white/16 disabled:cursor-not-allowed disabled:opacity-60" disabled={saving}>
              <Icon path="M6 18L18 6M6 6l12 12" className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <InputBlock label="Cliente">
                <div className="space-y-3">
                  <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
                    <button
                      type="button"
                      onClick={() => setForm((current) => ({ ...current, clientMode: 'existing' }))}
                      className={`rounded-2xl px-3 py-2 text-xs font-semibold transition ${!isCreatingClient ? 'bg-cyan-50 text-cyan-800' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                      Elegir existente
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm((current) => ({ ...current, clientMode: 'new', clientId: '' }))}
                      className={`rounded-2xl px-3 py-2 text-xs font-semibold transition ${isCreatingClient ? 'bg-violet-50 text-violet-800' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                      Crear nuevo
                    </button>
                  </div>

                  {!isCreatingClient ? (
                    <select value={form.clientId} onChange={handleChange('clientId')} className={baseFieldClassName} required={!isCreatingClient}>
                      <option value="">Elegí un cliente</option>
                      {sortedClients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {buildClientDisplayName(client)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="space-y-3 rounded-[22px] border border-violet-200 bg-violet-50/50 p-4">
                      <input value={form.newClientName} onChange={handleChange('newClientName')} className={baseFieldClassName} placeholder="Nombre del cliente o empresa" required={isCreatingClient} />
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <input value={form.newClientContactName} onChange={handleChange('newClientContactName')} className={baseFieldClassName} placeholder="Persona de contacto (opcional)" />
                        <input value={form.newClientPhone} onChange={handleChange('newClientPhone')} className={baseFieldClassName} placeholder="Teléfono (opcional)" />
                      </div>
                      <input type="email" value={form.newClientEmail} onChange={handleChange('newClientEmail')} className={baseFieldClassName} placeholder="Correo electrónico (opcional)" />
                      <div className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-xs leading-5 text-violet-800">
                        La dirección del destino principal también queda asociada al cliente para usarla luego desde la base.
                      </div>
                    </div>
                  )}
                </div>
              </InputBlock>
              <InputBlock label="Modalidad">
                <select value={form.serviceMode} onChange={handleChange('serviceMode')} className={baseFieldClassName}>
                  {SERVICE_MODE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </InputBlock>
                <InputBlock label="Transporte">
                  <select value={form.serviceTransport} onChange={handleChange('serviceTransport')} className={baseFieldClassName}>
                  {SERVICE_TRANSPORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </InputBlock>
              <InputBlock label="Frecuencia">
                <select value={form.frequency} onChange={handleChange('frequency')} className={baseFieldClassName}>
                  {recurringFrequencyOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </InputBlock>
              <InputBlock label="Prioridad">
                <select value={form.priority} onChange={handleChange('priority')} className={baseFieldClassName}>
                  {SERVICE_PRIORITY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </InputBlock>
            </div>

            <div className="rounded-[24px] border border-cyan-200 bg-cyan-50/70 px-5 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-700">Lectura comercial</p>
              <p className="mt-2 text-sm leading-6 text-cyan-950">
                Acá no cargamos un precio manual. Cuando el recorrido quede consolidado con origen, destinos y transporte,
                la app va a mostrar costo de viaje, posible ganancia y total sugerido para cobrarle al cliente.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <EndpointSection title="Origen" state={originState} onChange={setOriginState} locationOptions={locationOptions} tone="origin" />
              <div>
                <EndpointSection title="Destino" state={destinationState} onChange={setDestinationState} locationOptions={locationOptions} tone="destination" />
                <StopActionFields state={destinationState} onChange={setDestinationState} />
              </div>
            </div>

            <div className="rounded-[26px] border border-amber-200 bg-[linear-gradient(135deg,#fffbeb_0%,#ffffff_100%)] p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-black tracking-tight text-slate-900">Destinos extra</p>
                  <p className="mt-1 text-sm text-slate-500">Si este servicio pasa por varios puntos, podés agregarlos ahora.</p>
                </div>
                <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-800">
                  Paradas adicionales
                </span>
                <button
                  type="button"
                  onClick={() => setAdditionalStops((current) => [...current, createAdditionalStopState()])}
                  className="inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-700"
                >
                  <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                  Agregar destino
                </button>
              </div>

              {additionalStops.length > 0 ? (
                <div className="mt-4 space-y-4">
                  {additionalStops.map((stop, index) => (
                    <div key={stop.id} className="rounded-[24px] border border-amber-200 bg-white/95 p-4 shadow-sm">
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-bold text-slate-900">Destino extra {index + 1}</p>
                          <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">Parada adicional</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setAdditionalStops((current) => current.filter((item) => item.id !== stop.id))}
                          className="inline-flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-100"
                        >
                          <Icon path="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
                          Quitar
                        </button>
                      </div>
                      <EndpointSection
                        title={`Parada ${index + 1}`}
                        state={stop}
                        onChange={(updater) =>
                          setAdditionalStops((current) =>
                            current.map((item) =>
                              item.id !== stop.id
                                ? item
                                : typeof updater === 'function'
                                ? updater(item)
                                : updater
                            )
                          )
                        }
                        locationOptions={locationOptions}
                        tone="destination"
                      />
                      <StopActionFields
                        state={stop}
                        title={`Acción en parada ${index + 1}`}
                        onChange={(updater) =>
                          setAdditionalStops((current) =>
                            current.map((item) =>
                              item.id !== stop.id
                                ? item
                                : typeof updater === 'function'
                                  ? updater(item)
                                  : updater
                            )
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 rounded-[22px] border border-dashed border-amber-300 bg-white px-4 py-4 text-sm text-slate-500">
                  Por ahora el servicio queda en origen + destino final. Si necesitás más paradas, las sumás desde acá.
                </div>
              )}
            </div>

            <InputBlock label="Observación" helper="Referencia interna para repetir este servicio sin volver a pensar el circuito.">
              <textarea value={form.notes} onChange={handleChange('notes')} className={`${baseFieldClassName} min-h-[110px] resize-y`} placeholder="Ejemplo: retiro semanal de documentación o entrega fija a depósito tercero." />
            </InputBlock>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
            <button type="button" onClick={onClose} disabled={saving} className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60">
              <Icon path={saving ? 'M12 6v6l4 2.5' : 'M12 4.5v15m7.5-7.5h-15'} className="h-4 w-4" />
              {saving ? 'Guardando plantilla...' : initialTemplate ? 'Guardar cambios' : 'Guardar plantilla'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceTemplateModal;
